import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { US_TIMEZONES } from "@/components/status";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Home, Trash2, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SAMPLE_PHOTOS = [
  "https://images.unsplash.com/photo-1613575831056-0acd5da8f085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGVhbiUyMGFwYXJ0bWVudCUyMGludGVyaW9yfGVufDB8fHx8MTc4OTM2MjA5MXww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1665249934445-1de680641f50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMGFwYXJ0bWVudCUyMGludGVyaW9yfGVufDB8fHx8MTc4OTM2MjA5MXww&ixlib=rb-4.1.0&q=85",
];

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", notes: "", bedrooms: 1, bathrooms: 1, timezone: "America/Los_Angeles" });

  const load = useCallback(() => api.get("/properties").then((r) => setProperties(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const photo = SAMPLE_PHOTOS[properties.length % SAMPLE_PHOTOS.length];
      await api.post("/properties", { ...form, bedrooms: Number(form.bedrooms), bathrooms: Number(form.bathrooms), photos: [photo] });
      toast.success("Property added");
      setOpen(false);
      setForm({ name: "", address: "", notes: "", bedrooms: 1, bathrooms: 1, timezone: "America/Los_Angeles" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/properties/${id}`);
    toast.success("Property removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="properties-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Properties</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage the listings you turn over.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-ocean hover:bg-ocean/90" data-testid="add-property-button"><Plus className="w-4 h-4 mr-1" /> Add Property</Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-heading">New Property</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4" data-testid="property-form">
              <div>
                <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Name</Label>
                <Input required value={form.name} onChange={set("name")} placeholder="Downtown Loft" data-testid="property-name" />
              </div>
              <div>
                <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Address</Label>
                <Input required value={form.address} onChange={set("address")} placeholder="123 Main St, Austin, TX" data-testid="property-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Bedrooms</Label>
                  <Input type="number" min={0} value={form.bedrooms} onChange={set("bedrooms")} data-testid="property-bedrooms" />
                </div>
                <div>
                  <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Bathrooms</Label>
                  <Input type="number" min={0} value={form.bathrooms} onChange={set("bathrooms")} data-testid="property-bathrooms" />
                </div>
              </div>
              <div>
                <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Access Notes</Label>
                <Textarea value={form.notes} onChange={set("notes")} placeholder="Lockbox code, parking, pets…" data-testid="property-notes" />
              </div>
              <div>
                <Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Time Zone</Label>
                <select value={form.timezone} onChange={set("timezone")} data-testid="property-timezone"
                  className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
                  {US_TIMEZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="bg-ocean hover:bg-ocean/90" data-testid="property-submit">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Property"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {properties.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">
          <Home className="w-8 h-8 mx-auto mb-3 opacity-40" />
          No properties yet. Add one to start scheduling cleans.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {properties.map((p) => (
            <div key={p.id} className="border border-border rounded-md bg-white overflow-hidden group" data-testid={`property-card-${p.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                {p.photos?.[0] && <img src={p.photos[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <h3 className="font-heading font-bold text-lg text-sapphire">{p.name}</h3>
                  <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-red-600 transition-colors" data-testid={`delete-property-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {p.address}</div>
                <div className="text-xs text-muted-foreground mt-2">{p.bedrooms} bd · {p.bathrooms} ba</div>
                {p.notes && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
